import { useState } from "react";
import { useLowStockConsumables } from "@/common/api/use-low-stock-consumables";
import { useLowStockModels } from "@/common/api/use-low-stock-models";
import { LayoutList, Monitor, Loader2 } from "lucide-react";

type Tab = "consumables" | "devices";

export function LowStockPanel() {
  const [tab, setTab] = useState<Tab>("consumables");
  const consumables = useLowStockConsumables();
  const models = useLowStockModels();

  const isLoading = tab === "consumables" ? consumables.isLoading : models.isLoading;

  return (
    <div className="flex h-full min-h-0 w-full flex-col space-y-2">
      <div className="flex shrink-0 items-center justify-between px-1">
        <div className="border-border flex w-full overflow-hidden rounded-lg border text-xs">
          <button
            type="button"
            onClick={() => setTab("consumables")}
            className={`flex flex-1 items-center justify-center gap-1 px-2.5 py-2.5 transition-colors ${
              tab === "consumables"
                ? "bg-primary text-primary-foreground"
                : "bg-card text-muted-foreground hover:bg-accent"
            }`}
          >
            <LayoutList className="h-3 w-3" />
            Verbrauchsgüter
          </button>
          <button
            type="button"
            onClick={() => setTab("devices")}
            className={`flex flex-1 items-center justify-center gap-1 px-2.5 py-2.5 transition-colors ${
              tab === "devices" ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-accent"
            }`}
          >
            <Monitor className="h-3 w-3" />
            Geräte
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
        </div>
      ) : tab === "consumables" ? (
        <ConsumablesList items={consumables.items} />
      ) : (
        <ModelsList items={models.items} />
      )}
    </div>
  );
}

function ConsumablesList({ items }: { items: ReturnType<typeof useLowStockConsumables>["items"] }) {
  if (items.length === 0) {
    return (
      <div className="border-border bg-card text-muted-foreground flex flex-1 items-center justify-center rounded-xl border text-sm shadow-sm">
        Alles auf Lager
      </div>
    );
  }

  return (
    <div className="border-border bg-card divide-border min-h-0 flex-1 divide-y overflow-y-auto rounded-xl border shadow-sm">
      {items.map((item) => (
        <div key={item.id} className="flex items-center justify-between px-4 py-2.5">
          <div className="flex min-w-0 flex-1 items-center pr-2">
            <span className="text-foreground truncate text-sm font-medium">{item.name}</span>
            {item.category && (
              <span className="text-muted-foreground ml-2 shrink-0 truncate text-xs">{item.category.name}</span>
            )}
          </div>
          <div className="ml-2 flex shrink-0 items-baseline gap-1.5">
            <span
              className={`text-sm font-bold tabular-nums ${item.remaining < item.min_amt ? "text-destructive" : "text-amber-500"}`}
            >
              {item.remaining}
            </span>
            <span className="text-muted-foreground text-xs">/ {item.min_amt}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function ModelsList({ items }: { items: ReturnType<typeof useLowStockModels>["items"] }) {
  if (items.length === 0) {
    return (
      <div className="border-border bg-card text-muted-foreground flex flex-1 items-center justify-center rounded-xl border text-sm shadow-sm">
        Alles auf Lager
      </div>
    );
  }

  return (
    <div className="border-border bg-card divide-border min-h-0 flex-1 divide-y overflow-y-auto rounded-xl border shadow-sm">
      {items.map((item) => (
        <div key={item.id} className="flex items-center justify-between px-4 py-2.5">
          <div className="flex min-w-0 flex-1 pr-2">
            <span className="text-foreground block truncate text-sm font-medium">{item.name}</span>
          </div>
          <div className="ml-2 flex shrink-0 items-baseline gap-1.5">
            <span
              className={`text-sm font-bold tabular-nums ${(item.remaining ?? 0) < (item.min_amt ?? 0) ? "text-destructive" : "text-amber-500"}`}
            >
              {item.remaining ?? 0}
            </span>
            <span className="text-muted-foreground text-xs">/ {item.min_amt ?? 0}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
